<template>
  <Card class="card-size atb-ml-5 atb-mr-5 atb-pt-6">
    <template #body>
      <div v-show="importFileChooser">
        <div
          class="atb-flex atb-align-center atb-align-center tools-header-tab"
        >
          <div class="atb-font-bold atb-px-4 atb-py-4">
            {{ $t('fileToImport') }}
          </div>
          <FormField
            required="true"
            class="flex atb-w-4/6"
            :message="invalidFileTypeError"
            :message-type="'error'"
            message-class="atb-ml-5 atb-w-1/6"
          >
            <Textbox
              id="validateChooseFile"
              v-model="datafile"
              input-type="file"
              accept="*/*"
              @change="onSingleImportFileChange($event)"
            />
          </FormField>
        </div>
        <div class="datasources-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('dataSources') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="dsColumnDefs"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onDatasourceGridReady"
              @row-selected="onDataSourceRowSelected"
            />
          </div>
        </div>
      </div>
      <div v-show="importValidator">
        <div class="datasources-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('importData') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '400px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="importColumnDefs"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onImportDataGridReady"
            />
          </div>
        </div>
      </div>
      <div class="atb-flex atb-flex-row atb-justify-end atb-align atb-gap-x-8">
        <Button
          id="importBackButton"
          motif="primary"
          label="Back"
          :title="$t('back')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableBackButton"
          @click="onBackButtonClick"
        >
          {{ $t('back') }}
        </Button>
        <Button
          id="importNextButton"
          motif="primary"
          label="Next"
          :title="$t('next')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableNextButton"
          @click="onNextButtonClick"
        >
          {{ $t('next') }}
        </Button>
        <Button
          id="importButton"
          motif="primary"
          label="Import"
          :title="$t('import')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableImportButton"
          @click="onImportButtonClick"
        >
          {{ $t('import') }}
        </Button>
      </div>
    </template>
  </Card>
  <StreamingProgressModal
    :show="showProgressModal"
    :header-text="modalHeaderText"
    :message="modalBodyText"
    :percent="progressPercent"
    :is-closable="true"
    @on-modal-close="showProgressModal = false"
  />
</template>

<script lang="ts" setup>
  import { Ref, computed, nextTick, ref, watch } from 'vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import StreamingProgressModal from '@/app/shared/components/StreamingProgressModal.vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { FileExtensions } from '@/app/shared/enums/file-extension.enum';
  import { PopupModalEvent } from '@/app/shared/enums/popup-events.enum';
  import { AgGridEvent, GridApi } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { useI18n } from 'vue-i18n';
  import SystemApi from '@/app/features/ams/systems/systems-service';
  import type { ImportProgressEvent } from '@/app/features/ams/systems/systems-service';
  import { ImportCompletionData } from '@/app/shared/types/models/import-complete.interface';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import useSessionTracker from '@/app/shared/hooks/use-session-tracker';

  const { t } = useI18n();

  // file chooser
  const datafile: Ref<string> = ref('');
  const invalidFileTypeError = ref('');
  const selectedFileName = ref('');

  // page sections
  const importFileChooser = ref(true);
  const importValidator = ref(false);

  // footer buttons
  const disableBackButton = ref(true);
  const disableNextButton = ref(true);
  const disableImportButton = ref(true);

  const clientStore = useClientStore();
  const client = clientStore.getSelectedClient;
  const clientId = clientStore.selectedClient.id;
  const alertStore = useAlertStore();
  const sessionTracker = useSessionTracker();

  const dsGridApi = ref(null) as Ref<GridApi | null>;
  const selectedDataSources: Ref<any[]> = ref([]);

  const importGridApi = ref(null) as Ref<GridApi | null>;
  const formData = ref(new FormData());

  // modal
  const modalHeaderText = ref('');
  const modalBodyText = ref('');

  // Progress bar
  const showProgressModal = ref(false);
  const progressPercent = ref(0);

  // Sets value on 1st API Call (upload file)
  const bigCacheId = ref<number>(0);

  const importRowData = ref();

  const dsRowData = computed(() => {
    return client.dataSources;
  });

  const importColumnDefs = ref([
    {
      field: 'shortName',
      headerName: t('shortName'),
      sortable: true,
      minWidth: 200,
    },
    {
      field: 'cmiType',
      headerName: t('type'),
      suppressSizeToFit: false,
    },
    {
      field: 'datasource',
      headerName: t('dataSource'),
      suppressSizeToFit: false,
    },
    {
      headerName: t('overwrite'),
      field: 'collision',
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: (params: any) => {
        return params.data.collision;
      },
      cellRenderer: () => {
        return '';
      },
    },
  ]);

  const dsColumnDefs = ref([
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('dataSourceHistory'),
      suppressSizeToFit: true,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      suppressSizeToFit: false,
    },
  ]);

  const onDatasourceGridReady = (params: AgGridEvent) => {
    dsGridApi.value = params.api;
    dsGridApi.value.setRowData(dsRowData.value);
    dsGridApi.value.sizeColumnsToFit();
  };

  const onImportDataGridReady = (params: AgGridEvent) => {
    importGridApi.value = params.api;
    importGridApi.value.setRowData(importRowData.value);
    importGridApi.value.sizeColumnsToFit();
  };

  const onDataSourceRowSelected = () => {
    const selectedData = dsGridApi.value?.getSelectedNodes();
    selectedDataSources.value =
      selectedData?.map((node) => node.data.name) || [];
  };

  const onSingleImportFileChange = (event: any) => {
    if (formData.value.has('atbFile')) {
      formData.value.delete('atbFile');
    }
    const fileToUpload = event.target.files[0];

    if (fileToUpload) {
      const fileExtension = fileToUpload.name.split('.').pop();
      if (fileExtension === FileExtensions.ATB_FILE) {
        disableNextButton.value = false;
        invalidFileTypeError.value = '';
        selectedFileName.value = fileToUpload.name;
        formData.value.append('atbFile', fileToUpload, fileToUpload.name);
      } else {
        disableNextButton.value = true;
        invalidFileTypeError.value = t('invalidFileTypeError1');
        alertStore.showNotification(
          t('invalidFileTypeError2'),
          'Import',
          Motif.ERROR
        );
      }
    }
  };

  const onBackButtonClick = () => {
    importFileChooser.value = true;
    importValidator.value = false;
    disableBackButton.value = true;
    if (datafile.value) {
      disableNextButton.value = false;
    }
  };

  const onNextButtonClick = () => {
    modalHeaderText.value = t('verifyingImportFile');
    modalBodyText.value = t('fileVerificationMessage');
    validateImportFile();
  };

  const onImportButtonClick = () => {
    modalHeaderText.value = t('importingData');
    modalBodyText.value = t('dataImportMessage');
    importImportFile();
  };

  const validateImportFile = async () => {
    showProgressModal.value = true;

    if (formData.value.has('importUploadRequest')) {
      formData.value.delete('importUploadRequest');
    }
    const dataSourceJson = new Blob(
      [
        JSON.stringify({
          datasources: selectedDataSources.value,
        }),
      ],
      {
        type: 'application/json',
      }
    );

    formData.value.append('importUploadRequest', dataSourceJson, '');

    try {
      const {
        data: { data },
      } = await SystemApi.validateImportFile(clientId, formData.value);
      importRowData.value = data.collisions;
      bigCacheId.value = data.bigCacheId;
      if (data.warnings && data.warnings.length > 0) {
        let datasources = '';
        data.warnings.forEach((ds, i) => {
          if (i === data.warnings.length - 1) {
            datasources += ds;
          } else {
            datasources += ds + ', ';
          }
        });
        alertStore.showPopupModal(
          t('importWarningMessageTitle'),
          t('importWarningMessage') + datasources
        );
      }
      importGridApi.value?.setRowData(importRowData.value);
      importFileChooser.value = false;
      importValidator.value = true;
      disableBackButton.value = false;
      disableNextButton.value = true;
      disableImportButton.value = false;
    } catch (error: any) {
      alertStore.showNotification(
        error.errors[0].message,
        'Import',
        Motif.ERROR
      );
    }

    nextTick();
    // Enable/Disable row selection based on collision
    importGridApi.value?.forEachNode((node) => {
      node.selectable = node.data.collision;
    });
    showProgressModal.value = false;
  };

  const computeOverallPercent = (event: ImportProgressEvent): number => {
    if (event.total <= 0) return 0;
    return Math.round((event.current / event.total) * 100);
  };

  const importImportFile = async () => {
    showProgressModal.value = true;
    progressPercent.value = 0;
    const rowData: Array<ImportCompletionData> = [];
    importGridApi.value?.getSelectedRows().forEach((node) => {
      rowData.push({
        shortName: node.shortName,
        datasource: node.datasource,
        overwrite: true,
      });
    });
    let apiStatus = false;
    try {
      sessionTracker.toggleTimeout();
      await SystemApi.completeImportFileData(
        clientId,
        {
          bigCacheId: bigCacheId.value,
          overwrites: rowData,
        },
        (event: ImportProgressEvent) => {
          modalBodyText.value = event.message;
          progressPercent.value = computeOverallPercent(event);
        }
      );
      apiStatus = true;
      sessionTracker.toggleTimeout();
    } catch (error: unknown) {
      apiStatus = false;
      console.error('Import failed:', error);
    }
    showProgressModal.value = false;
    progressPercent.value = 0;
    alertStore.showPopupModal(
      apiStatus ? t('importSuccessTitle') : t('importFailureTitle'),
      apiStatus
        ? t('importSuccessText', {
            value: selectedFileName.value,
          })
        : t('importFailureText', {
            value: selectedFileName.value,
          })
    );
  };

  watch(
    () => alertStore.popupButtonEvent,
    (newValue: PopupModalEvent) => {
      if (
        newValue == PopupModalEvent.OK_BUTTON_EVENT &&
        alertStore.popupTitle !== t('importWarningMessageTitle')
      ) {
        resetImportFileAndGrid();
      } else if (
        newValue == PopupModalEvent.CLOSE_BUTTON_EVENT &&
        alertStore.popupTitle !== t('importWarningMessageTitle')
      ) {
        resetImportOnModalClose();
      }
    }
  );

  const resetImportFileAndGrid = () => {
    datafile.value = '';
    dsGridApi.value?.deselectAll();
    selectedDataSources.value.length = 0;
    formData.value = new FormData();
    importRowData.value = [];

    disableBackButton.value = true;
    disableNextButton.value = true;
    disableImportButton.value = true;

    importValidator.value = false;
    importFileChooser.value = true;
  };

  const resetImportOnModalClose = () => {
    disableBackButton.value = false;
    disableNextButton.value = true;
    disableImportButton.value = true;

    importValidator.value = true;
    importFileChooser.value = false;
  };
</script>

<style scoped>
  .tools-header-tab {
    align-items: center;
    height: 50px;
  }
  .datasources-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }
  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }
</style>
